import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { getMetrics } from "../../../util/metricsAPI";
import { getTrialInfo } from "../../../util/trialApi";
import { chartActions } from "../../../store/chart-slice";
import { formatSensorMetricLabel, resolveSensorMetricEntries } from "../../../util/sensorMetrics";
import { filterPlottableAngleMetrics } from "../../../util/floorContacts";
import { LEGACY_ANGLE_METRICS } from "../../../util/legacyAnalytics";
import { groupLegacySensors } from "../../../util/legacySensors";

function MetricDropdown(props) {
  const selected_metrics = useSelector((state) => state.chart.list_metrics);
  const selected_sensor_metrics = useSelector((state) => state.chart.list_sensor_metrics) || [];
  const [allMetrics, setAllMetrics] = useState(null);
  const [allSensorMetrics, setAllSensorMetrics] = useState(null);
  const [legacySensorGroups, setLegacySensorGroups] = useState([]);
  const { trial, legacy = false } = props;
  const evowalkEnabled = !legacy && localStorage.getItem('evowalkIntegrationEnabled') === 'true';
  const dispatch = useDispatch();

  useEffect(() => {
    if (!trial) return;
    getMetrics(trial).then((res) => {
      const plottable = filterPlottableAngleMetrics(res && res.angles);
      // Legacy imports leave the other angle columns all null; do not offer empty charts.
      const names = legacy
        ? LEGACY_ANGLE_METRICS.filter((name) => plottable.includes(name))
        : plottable;
      const options = names.map((el) => ({
        label: el,
        value: el,
      }));
      setAllMetrics(options);
      setLegacySensorGroups(legacy ? groupLegacySensors(res && res.sensors) : []);
    }).catch(() => setAllMetrics([]));
  }, [trial, legacy]);

  useEffect(() => {
    if (!trial || !evowalkEnabled) return;
    getTrialInfo(trial).then((res) => {
      const trialName = res?.trial?.name || res?.trial?.date || null;
      getMetrics(trial, trialName).then((dict) => {
        const entries = resolveSensorMetricEntries(dict || {});
        const options = entries.map((entry) => ({
          label: entry.label,
          value: entry.apiName,
        }));
        setAllSensorMetrics(options);
      }).catch(() => setAllSensorMetrics([]));
    }).catch(() => setAllSensorMetrics([]));
  }, [trial, evowalkEnabled]);

  const handleAngleChange = (e) => {
    const next = e != null ? e.map((el) => el.value) : [];
    dispatch(chartActions.updateListMetrics(next));
  };
  const handleSensorChange = (e) => {
    const next = e != null ? e.map((el) => el.value) : [];
    dispatch(chartActions.updateListSensorMetrics(next));
  };

  const formatted_selected_metrics = (selected_metrics || []).map((el) => ({ label: el, value: el }));
  // Legacy channels keep their lab names; options are grouped per device.
  const legacySensorOptions = legacySensorGroups.map((group) => ({
    label: group.label,
    options: group.channels.map((entry) => ({
      label: legacySensorGroups.length > 1 ? `${entry.channel} · ${group.label}` : entry.channel,
      value: entry.name,
    })),
  }));
  const legacySensorLabels = new Map(
    legacySensorOptions.flatMap((group) => group.options.map((opt) => [opt.value, opt.label]))
  );
  const formatted_sensor_metrics = selected_sensor_metrics.map((el) => ({
    label: legacy ? legacySensorLabels.get(el) ?? el : formatSensorMetricLabel(el),
    value: el,
  }));

  const selectStyles = {
    control: (base) => ({ ...base, minWidth: '200px' }),
    menu: (base) => ({ ...base, zIndex: 15 }),
  };

  return (
    <div className="no-print" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <h4 style={{ marginRight: "20px" }}>Angle charts</h4>
        <Select
          styles={selectStyles}
          onChange={handleAngleChange}
          value={formatted_selected_metrics}
          isMulti
          options={allMetrics}
        />
      </div>
      {legacy && legacySensorOptions.length > 0 && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <h4 style={{ marginRight: "20px" }}>Sensor charts (EvoWalk)</h4>
          <Select
            styles={selectStyles}
            onChange={handleSensorChange}
            value={formatted_sensor_metrics}
            isMulti
            options={legacySensorOptions}
            aria-label="Sensor charts"
          />
        </div>
      )}
      {evowalkEnabled && allSensorMetrics && allSensorMetrics.length > 0 && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <h4 style={{ marginRight: "20px" }}>Sensor charts (EvoWalk)</h4>
          <Select
            styles={selectStyles}
            onChange={handleSensorChange}
            value={formatted_sensor_metrics}
            isMulti
            options={allSensorMetrics}
          />
        </div>
      )}
    </div>
  );
}

export default MetricDropdown;
